import React, { useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { t } from 'i18next';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { IncidenceTableDto } from 'apps/marsinet/src/app/typescript-axios';
import { MarsinetContext } from '../../../../../context/marsinetProvider';
import { getLocalDateTimeInTimeZone } from '../../../../../utils/timezone-utils';

export type CloseMaintenanceOrderData = {
  closeConfirmationAccepted: boolean;
  selectedDateTime: Date | null;
  resolvedIncidenceIds: string[];
  comment: string;
};

type CloseMaintenanceOrderDialogProps = {
  visible: boolean;
  loading?: boolean;
  downloadingPdf?: boolean;
  loadingIncidents?: boolean;

  maintenanceOrderId?: string;
  maintenanceOrderCode?: string;
  maintenanceOrderStatus?: string;

  associatedIncidents?: IncidenceTableDto[];

  materialImputationsCount?: number;
  timeImputationsCount?: number;
  allowManualClosingDate?: boolean;

  isCorrective: boolean;

  onCancel: () => void;
  /**
   * Debe resolver a `true` si la OM se ha cerrado correctamente.
   * Solo entonces el diálogo pasa al paso de descarga de PDF.
   */
  onConfirm: (closeData: CloseMaintenanceOrderData) => Promise<boolean>;
  onDownloadPdf: (maintenanceOrderId: string) => void;
};

type SectionProps = {
  icon: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

const MAX_COMMENT_LENGTH = 500;

const Section: React.FC<SectionProps> = ({
  icon,
  title,
  subtitle,
  badge,
  children,
  className = '',
}) => (
  <section
    className={`surface-card border-1 surface-border border-round-xl overflow-hidden ${className}`}
  >
    <div
      className="flex align-items-start justify-content-between gap-3 p-3 border-round-lg mb-4"
      style={{
        backgroundColor: '#004e78',
        border: '1px solid #ffffff',
      }}
    >
      <div className="flex align-items-start gap-3 min-w-0">
        <span
          className="flex align-items-center justify-content-center border-circle flex-shrink-0"
          style={{
            width: '2.5rem',
            height: '2.5rem',
            backgroundColor: '#ccfbf1',
            color: '#0f766e',
          }}
        >
          <i className={icon} aria-hidden="true" />
        </span>

        <div className="flex flex-column gap-1 min-w-0">
          <span className="font-semibold text-900  text-white text-lg">
            {title}
          </span>

          {subtitle && (
            <span className="text-base text-600 text-white line-height-3">
              {subtitle}
            </span>
          )}
        </div>
      </div>

      {badge}
    </div>

    <div className="p-4">{children}</div>
  </section>
);

export const CloseMaintenanceOrderDialog: React.FC<
  CloseMaintenanceOrderDialogProps
> = ({
  visible,
  loading = false,
  downloadingPdf = false,
  loadingIncidents = false,
  maintenanceOrderId,
  maintenanceOrderCode,
  maintenanceOrderStatus,
  associatedIncidents = [],
  materialImputationsCount = 0,
  timeImputationsCount = 0,
  allowManualClosingDate = false,
  isCorrective,
  onCancel,
  onConfirm,
  onDownloadPdf,
}) => {
  const { selectedTimezone } = useContext(MarsinetContext);
  const [step, setStep] = React.useState<'form' | 'closed'>('form');
  // Muestra la hora de cierre según la zona seleccionada.
  const [selectedDateTime, setSelectedDateTime] = React.useState<Date | null>(
    getLocalDateTimeInTimeZone(new Date(), selectedTimezone)
  );
  const [resolvedIncidenceIds, setResolvedIncidenceIds] = React.useState<
    string[]
  >([]);
  const [comment, setComment] = React.useState('');
  const [validationError, setValidationError] = React.useState<string | null>(
    null
  );
  const [commentError, setCommentError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (visible) {
      setStep('form');
      setSelectedDateTime(
        getLocalDateTimeInTimeZone(new Date(), selectedTimezone)
      );
      setResolvedIncidenceIds([]);
      setComment('');
      setValidationError(null);
      setCommentError(null);
    }
  }, [visible, selectedTimezone]);

  const hasAssociatedIncidents = associatedIncidents.length > 0;
  const totalImputations = materialImputationsCount + timeImputationsCount;

  // Correctiva: obligatorio cerrar con al menos una incidencia resuelta.
  // Preventiva/programada: se puede cerrar sin resolver ninguna, tenga o no incidencias asociadas.
  const canConfirm = isCorrective ? resolvedIncidenceIds.length > 0 : true;

  // El comentario (CloseIncidenceDto) solo hace falta si de verdad se van a cerrar incidencias.
  const commentRequired = resolvedIncidenceIds.length > 0;

  const toggleResolved = (incidentId: string) => {
    setResolvedIncidenceIds((previousIds) =>
      previousIds.includes(incidentId)
        ? previousIds.filter((id) => id !== incidentId)
        : [...previousIds, incidentId]
    );
    setValidationError(null);
  };

  const handleConfirmClick = async () => {
    const trimmedComment = comment.trim();
    let hasError = false;

    if (isCorrective && resolvedIncidenceIds.length === 0) {
      setValidationError(
        associatedIncidents.length === 1
          ? t(
              'pages.maintenance_orders.closeDialog.singleIncidentRequiredError'
            )
          : t('pages.maintenance_orders.closeDialog.noIncidentSelectedError')
      );
      hasError = true;
    }

    if (commentRequired && !trimmedComment) {
      setCommentError(
        t('pages.maintenance_orders.closeDialog.commentRequiredError')
      );
      hasError = true;
    }

    if (hasError) return;

    const closedSuccessfully = await onConfirm({
      closeConfirmationAccepted: true,
      selectedDateTime,
      resolvedIncidenceIds,
      comment: trimmedComment,
    });

    if (closedSuccessfully) {
      setStep('closed');
    }
  };

  const formatClosingDateTime = (date?: Date | null) => {
    if (!date) return '';

    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const footer =
    step === 'form' ? (
      <div className="flex flex-column-reverse sm:flex-row justify-content-end gap-2 pt-2">
        <Button
          label="Cancelar"
          icon="pi pi-times"
          severity="secondary"
          outlined
          disabled={loading}
          onClick={onCancel}
        />
        <Button
          label="Aceptar y cerrar OM"
          icon="pi pi-check"
          severity="danger"
          loading={loading}
          disabled={loadingIncidents || !canConfirm}
          tooltip={
            canConfirm
              ? undefined
              : t(
                  'pages.maintenance_orders.closeDialog.noIncidentSelectedError'
                )
          }
          tooltipOptions={{ position: 'top', showOnDisabled: true }}
          onClick={handleConfirmClick}
        />
      </div>
    ) : (
      <div className="flex flex-column-reverse sm:flex-row justify-content-end gap-2 pt-2">
        <Button
          label="Cerrar"
          icon="pi pi-times"
          severity="secondary"
          outlined
          onClick={onCancel}
        />
        <Button
          label="Descargar PDF"
          icon="pi pi-download"
          severity="success"
          loading={downloadingPdf}
          disabled={!maintenanceOrderId}
          onClick={() =>
            maintenanceOrderId && onDownloadPdf(maintenanceOrderId)
          }
        />
      </div>
    );

  return (
    <Dialog
      visible={visible}
      modal
      draggable={false}
      resizable={false}
      header={
        <div className="w-full text-center">
          <span className="text-xl font-semibold">
            {t('pages.maintenance_orders.closeDialog.closeTitle')}
          </span>
        </div>
      }
      footer={footer}
      style={{ width: '80rem', maxWidth: '96vw' }}
      breakpoints={{
        '1200px': '85vw',
        '960px': '92vw',
        '640px': '96vw',
      }}
      contentStyle={{ paddingTop: '0.75rem' }}
      onHide={() => {
        if (!loading) onCancel();
      }}
    >
      {step === 'closed' ? (
        <div className="flex flex-column gap-3 text-lg">
          <div className="flex align-items-start gap-3 p-4 border-round-xl bg-green-50 border-1 border-green-200">
            <i
              className="pi pi-check-circle text-green-600 mt-1"
              style={{ fontSize: '1.5rem' }}
              aria-hidden="true"
            />
            <div className="flex flex-column gap-2">
              <span className="font-semibold text-900 text-lg">
                Orden de mantenimiento cerrada
              </span>
              <span className="text-sm text-700 line-height-3">
                {maintenanceOrderCode
                  ? `La OM ${maintenanceOrderCode} se ha cerrado correctamente.`
                  : 'La orden se ha cerrado correctamente.'}{' '}
                Ya puedes descargar el PDF.
              </span>

              {resolvedIncidenceIds.length > 0 && (
                <span className="text-sm text-700 line-height-3">
                  {resolvedIncidenceIds.length === 1
                    ? 'También se ha cerrado 1 incidencia asociada.'
                    : `También se han cerrado ${resolvedIncidenceIds.length} incidencias asociadas.`}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          {/* dialogo de alarma cierre */}
          <div className="flex align-items-start gap-3 p-4 border-round-xl bg-orange-50 border-1 border-orange-200">
            <span
              className="flex align-items-center justify-content-center border-circle bg-orange-100 text-orange-600 flex-shrink-0"
              style={{ width: '2.5rem', height: '2.5rem' }}
            >
              <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            </span>

            <div className="flex flex-column gap-2 min-w-0">
              <span className="font-semibold text-900">
                {t('pages.maintenance_orders.closeDialog.title')}
              </span>

              <span className="text-sm text-700 line-height-3">
                {t('pages.maintenance_orders.closeDialog.description')}
              </span>

              <div className="flex align-items-start gap-2 pt-2 mt-1 border-top-1 border-orange-200">
                <i
                  className="pi pi-lock text-orange-600 mt-1 flex-shrink-0"
                  aria-hidden="true"
                />

                <span className="text-sm font-medium text-800 line-height-3">
                  {t(
                    'pages.maintenance_orders.closeDialog.closeActionDescription'
                  )}
                </span>
              </div>
            </div>
          </div>

          <Section
            icon="pi pi-wrench"
            title={t('pages.maintenance_orders.closeDialog.title')}
            subtitle={t('pages.maintenance_orders.closeDialog.subtitle')}
          >
            <div className="grid formgrid">
              <div className="col-12 md:col-6 mb-3 md:mb-0">
                <span className="block text-xs font-semibold text-500 uppercase mb-2">
                  {t('pages.maintenance_orders.closeDialog.orderLabel')}
                </span>
                <div className="flex align-items-center gap-2 min-h-2rem">
                  <i className="pi pi-hashtag text-500" aria-hidden="true" />
                  <span className="font-semibold text-900  text-xl">
                    {maintenanceOrderCode ?? '—'}
                  </span>
                </div>
              </div>

              <div className="col-12 md:col-6">
                <span className="block text-xs font-semibold text-500 uppercase mb-2">
                  {t('pages.maintenance_orders.closeDialog.statusActual')}
                </span>
                <span className="inline-flex align-items-center gap-2 px-3 py-2 border-round-lg surface-100 text-700 font-medium">
                  <i className="pi pi-circle-fill text-xs text-primary" />
                  {maintenanceOrderStatus ?? '—'}
                </span>
              </div>

              <div className="col-12 mt-4">
                <label
                  htmlFor="maintenance-order-closing-date"
                  className="block text-sm font-medium text-700 mb-2"
                >
                  {t('pages.maintenance_orders.closeDialog.closingDateTime')}
                </label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-calendar" />
                  </span>
                  <InputText
                    id="maintenance-order-closing-date"
                    value={formatClosingDateTime(selectedDateTime)}
                    readOnly
                    className="w-full"
                  />
                </div>
                {!allowManualClosingDate && (
                  <small className="block text-600 mt-2 line-height-3">
                    {t(
                      'pages.maintenance_orders.closeDialog.confirmDescription'
                    )}
                  </small>
                )}
              </div>
            </div>
          </Section>
          {(loadingIncidents || hasAssociatedIncidents) && (
            <Section
              icon="pi pi-exclamation-circle"
              title={t(
                'pages.maintenance_orders.closeDialog.associatedIncidents'
              )}
              subtitle={t(
                'pages.maintenance_orders.closeDialog.confirmSingleResolved'
              )}
              badge={
                <span className="inline-flex align-items-center justify-content-center px-2 py-1 border-round-xl bg-blue-50 text-blue-900 text-m font-semibold">
                  {loadingIncidents ? '…' : associatedIncidents.length}
                </span>
              }
              className={
                validationError || commentError ? 'border-red-300' : ''
              }
            >
              {loadingIncidents ? (
                <div className="flex align-items-center justify-content-center gap-3 p-4 text-600">
                  <ProgressSpinner
                    style={{ width: '2rem', height: '2rem' }}
                    strokeWidth="4"
                  />
                  <span className="text-sm">
                    {t('pages.maintenance_orders.closeDialog.loadingIncidents')}
                  </span>
                </div>
              ) : (
                <div className="flex flex-column gap-2">
                  {associatedIncidents.map((incident) => {
                    const isResolved = resolvedIncidenceIds.includes(
                      incident.id
                    );

                    return (
                      <label
                        key={incident.id}
                        htmlFor={`incident-${incident.id}`}
                        className={`flex align-items-start gap-3 p-3 border-1 border-round-lg cursor-pointer transition-colors transition-duration-150 ${
                          isResolved
                            ? 'border-primary bg-primary-50'
                            : 'surface-border surface-ground'
                        }`}
                      >
                        <Checkbox
                          inputId={`incident-${incident.id}`}
                          checked={isResolved}
                          onChange={() => toggleResolved(incident.id)}
                          disabled={loading}
                        />

                        <div className="flex flex-column gap-1 min-w-0">
                          <span className="font-semibold text-900">
                            {incident.incident_code}
                          </span>
                          {incident.description && (
                            <span className="text-sm text-600 line-height-3">
                              {incident.description}
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}

                  {validationError && (
                    <div
                      className="flex align-items-center gap-2 mt-1 text-red-600 text-sm"
                      role="alert"
                    >
                      <i className="pi pi-info-circle" aria-hidden="true" />
                      <span>{validationError}</span>
                    </div>
                  )}

                  {/* Comentario de cierre de las incidencias seleccionadas */}
                  <div className="flex flex-column gap-2 mt-3 pt-3 border-top-1 surface-border">
                    <label
                      htmlFor="incidence-closing-comment"
                      className="block text-sm font-medium text-700"
                    >
                      {t('pages.maintenance_orders.closeDialog.closingComment')}
                      <span className="text-red-500 ml-1">*</span>
                    </label>

                    <InputTextarea
                      id="incidence-closing-comment"
                      value={comment}
                      onChange={(e) => {
                        setComment(e.target.value);
                        setCommentError(null);
                      }}
                      rows={3}
                      autoResize
                      disabled={loading}
                      maxLength={MAX_COMMENT_LENGTH}
                      placeholder={t(
                        'pages.maintenance_orders.closeDialog.closingCommentPlaceholder'
                      )}
                      className={`w-full ${commentError ? 'p-invalid' : ''}`}
                    />

                    <div className="flex align-items-start justify-content-between gap-3">
                      {commentError ? (
                        <small className="text-red-600" role="alert">
                          {commentError}
                        </small>
                      ) : (
                        <small className="text-600 line-height-3">
                          {t(
                            'pages.maintenance_orders.closeDialog.closingCommentHelp'
                          )}
                        </small>
                      )}

                      <small className="text-500 flex-shrink-0">
                        {comment.length}/{MAX_COMMENT_LENGTH}
                      </small>
                    </div>
                  </div>
                </div>
              )}
            </Section>
          )}

          <Section
            icon="pi pi-clock"
            title={t(
              'pages.maintenance_orders.closeDialog.associatedImputations'
            )}
            subtitle="Resumen de imputaciones registradas en esta OM."
            badge={
              <span className="inline-flex align-items-center justify-content-center px-2 py-1 border-round-xl bg-blue-50 text-blue-900 text-m font-semibold">
                {totalImputations}
              </span>
            }
          >
            <div className="grid">
              <div className="col-12 sm:col-6">
                <div className="flex align-items-center justify-content-between gap-3 p-3 border-1 surface-border border-round-lg surface-ground h-full">
                  <div className="flex align-items-center gap-3">
                    <span
                      className="flex align-items-center justify-content-center border-circle bg-blue-50 text-blue-600"
                      style={{ width: '2.5rem', height: '2.5rem' }}
                    >
                      <i className="pi pi-box" />
                    </span>
                    <span className="text-sm font-medium text-700">
                      {t(
                        'pages.maintenance_orders.closeDialog.materialImputations'
                      )}
                    </span>
                  </div>
                  <strong className="text-xl text-900">
                    {materialImputationsCount}
                  </strong>
                </div>
              </div>

              <div className="col-12 sm:col-6">
                <div className="flex align-items-center justify-content-between gap-3 p-3 border-1 surface-border border-round-lg surface-ground h-full">
                  <div className="flex align-items-center gap-3">
                    <span
                      className="flex align-items-center justify-content-center border-circle bg-orange-50 text-orange-600"
                      style={{ width: '2.5rem', height: '2.5rem' }}
                    >
                      <i className="pi pi-stopwatch" />
                    </span>
                    <span className="text-sm font-medium text-700">
                      {t(
                        'pages.maintenance_orders.closeDialog.timeImputations'
                      )}
                    </span>
                  </div>
                  <strong className="text-xl text-900">
                    {timeImputationsCount}
                  </strong>
                </div>
              </div>
            </div>
          </Section>
        </div>
      )}
    </Dialog>
  );
};
